/**
 * Data layer for the COMPANY panel's Shuru section.
 *
 * Shuru is the in-app AI companion. Its records are one row per MESSAGE, so
 * everything here is derived rather than counted directly — see
 * services/shuruHubService.js for what each number actually means. Two of them
 * are worth restating wherever they are rendered:
 *
 *  - "Felt better" comes from the skippable end-of-chat prompt, so its rate is
 *    among RATED chats, never among all of them.
 *  - Time is measured per user per day (first message to last), so it is time
 *    in conversation, not the length of a single chat.
 *
 * Backed by the company/v1 shuru-hub endpoints (token-scoped to the company;
 * the B2B/Members toggle is appended by api/companyApi.js). Every fetcher takes
 * an optional `department` so the section's filter can narrow the whole page.
 *
 * The adapter is exported so a super-admin Shuru hub, served by the same
 * builders, can reuse the field mapping rather than restating it.
 */

import {
  getCompanyShuruDashboard,
  getCompanyShuruUsers,
  getCompanyShuruUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Conversation block shared by every Shuru dashboard. */
export const adaptShuruConversations = (data) => ({
  chatters: data.chatters ?? 0,
  chats: data.chats ?? 0,
  messages: data.messages ?? 0,
  userMessages: data.userMessages ?? 0,
  botMessages: data.botMessages ?? 0,
  avgChatsPerUser: data.avgChatsPerUser ?? 0,
  messagesPerChat: data.messagesPerChat ?? 0,
  totalTimeMins: data.totalTimeMins ?? 0,
  activeDays: data.activeDays ?? 0,
  avgDailyMins: data.avgDailyMins ?? 0,
  avgTimePerUserMins: data.avgTimePerUserMins ?? 0,
  lastChatDate: data.lastChatDate || null,

  feedbackResponses: data.feedbackResponses ?? 0,
  feltBetter: data.feltBetter ?? 0,
  feltBetterRate: data.feltBetterRate ?? 0,
  feedbackSplit: data.feedbackSplit || [],
  feedbackChannels: data.feedbackChannels || [],

  positivitySplit: data.positivitySplit || [],
  positiveMessageRate: data.positiveMessageRate ?? 0,

  chatDepth: data.chatDepth || [],
  messageDepth: data.messageDepth || [],

  // Backend zero-fills the last 30 days; charts consume plain series.
  chatsTrend: (data.activityTrend || []).map((day) => day.chats || 0),
  messagesTrend: (data.activityTrend || []).map((day) => day.messages || 0),
  minutesTrend: (data.activityTrend || []).map((day) => day.minutes || 0),
  chattersTrend: (data.activityTrend || []).map((day) => day.chatters || 0),

  weekdayChats: data.weekdayChats || [],
  hourlyChats: data.hourlyChats || [],

  moodCheckIns: data.moodCheckIns || [],
  moodCheckInTotal: data.moodCheckInTotal ?? 0,
  hardMoodRate: data.hardMoodRate ?? 0,

  positiveThemes: data.positiveThemes || [],
  negativeThemes: data.negativeThemes || []
});

export const fetchCompanyShuruDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyShuruDashboard(department));
    return {
      ...adaptShuruConversations(data),
      // Company-only: the size of the population, so the page can say
      // "32 of 240 employees have talked to Shuru".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyShuruDashboard error:', error);
    return null;
  }
};

// Matches the ShuruUsersTable fetcher contract.
export const fetchCompanyShuruUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyShuruUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyShuruUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's Shuru profile ({ user, feedbackSplit, moodCheckIns,
 * dailyActivity }). Volume and outcome only — the endpoint never returns what
 * was said. The backend 404s any user outside this company, so no scoping is
 * needed here.
 */
export const fetchCompanyShuruUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyShuruUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyShuruUserDetail error:', error);
    return null;
  }
};
